/**
 * Which heading weights a font really has.
 *
 * The heading weight picker (Branding and the create wizard) offers Regular to
 * Extra bold, whatever the font. A weight the font lacks must not be left to
 * the browser: it fakes a bold by drawing each letter twice (very visible on
 * iPhone), or falls back to another font. So the page draws the nearest weight
 * the font really has, and when that is lighter than the one chosen, adds an
 * outline in the text's own colour to make up the difference
 * (headingWeightDrawing). The page asks Google only for weights that exist.
 *
 * Pure and client safe: the pickers and the public page both read it.
 */

import type { FontStyle, HeaderWeight } from '@fitvent/types';

/** Every picker weight, lightest first, with its CSS value. */
export const HEADING_WEIGHTS: ReadonlyArray<{ value: HeaderWeight; css: number }> = [
  { value: 'normal', css: 400 },
  { value: 'medium', css: 500 },
  { value: 'semibold', css: 600 },
  { value: 'bold', css: 700 },
  { value: 'extrabold', css: 800 },
];

const ALL: HeaderWeight[] = HEADING_WEIGHTS.map((w) => w.value);

export function headingWeightCss(weight: HeaderWeight | string | null | undefined): number {
  return HEADING_WEIGHTS.find((w) => w.value === weight)?.css ?? 700;
}

/**
 * The weights each Google font the pickers offer has, checked against the
 * Google Fonts css2 API (it answers 400 for a weight a family lacks). Keys are
 * lower case. Fonts the pickers don't list are unknown and keep every option.
 */
export const GOOGLE_FONT_WEIGHTS: Readonly<Record<string, readonly number[]>> = {
  'barlow condensed': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'bebas neue': [400],
  caprasimo: [400],
  'dm sans': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  geist: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  inter: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'instrument sans': [400, 500, 600, 700],
  'instrument serif': [400],
  'josefin sans': [100, 200, 300, 400, 500, 600, 700],
  lato: [100, 300, 400, 700, 900],
  merriweather: [300, 400, 500, 600, 700, 800, 900],
  montserrat: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  nunito: [200, 300, 400, 500, 600, 700, 800, 900],
  'open sans': [300, 400, 500, 600, 700, 800],
  'playfair display': [400, 500, 600, 700, 800, 900],
  'plus jakarta sans': [200, 300, 400, 500, 600, 700, 800],
  poppins: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'pt sans': [400, 700],
  quicksand: [300, 400, 500, 600, 700],
  raleway: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  roboto: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  sora: [100, 200, 300, 400, 500, 600, 700, 800],
  'space grotesk': [300, 400, 500, 600, 700],
  ubuntu: [300, 400, 500, 700],
  urbanist: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'varela round': [400],
  'work sans': [100, 200, 300, 400, 500, 600, 700, 800, 900],
};

/**
 * Fonts every device already has (the pickers list them too). They come in a
 * regular and a bold, and are never requested from Google.
 */
export const SYSTEM_FONT_WEIGHTS: Readonly<Record<string, readonly number[]>> = {
  arial: [400, 700],
  georgia: [400, 700],
  helvetica: [400, 700],
  tahoma: [400, 700],
  'times new roman': [400, 700],
  'trebuchet ms': [400, 700],
  verdana: [400, 700],
};

/** An uploaded font as stored in metadata.customUploadedFonts; weights are read from the file at upload. */
export interface UploadedFontWeights {
  name: string;
  /** Every weight the file has, in hundreds: one for a single master, a run for a variable font. Absent on older uploads. */
  weights?: number[];
}

const key = (family: string) => family.trim().replace(/^["']|["']$/g, '').toLowerCase();

/** True for a font on every device, which is never fetched from Google. */
export function isSystemFont(family: string | null | undefined): boolean {
  return !!family && key(family) in SYSTEM_FONT_WEIGHTS;
}

/**
 * The weights a font family has, or null when we don't know (a font from an
 * older import, an upload from before weights were read). An uploaded face,
 * when given, is the one drawn, whatever the family is called.
 */
export function fontWeightsOf(family: string | null | undefined, uploaded?: UploadedFontWeights | null): number[] | null {
  // Callers pass an upload only when it is the face being drawn.
  if (uploaded) {
    const w = (Array.isArray(uploaded.weights) ? uploaded.weights : []).filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 1 && n <= 1000);
    return w.length ? [...new Set(w)].sort((a, b) => a - b) : null;
  }
  if (!family) return null;
  const k = key(family);
  const known = GOOGLE_FONT_WEIGHTS[k] ?? SYSTEM_FONT_WEIGHTS[k];
  return known ? [...known] : null;
}

/**
 * The picker weights a font can draw. Every option when the font is unknown.
 * A font whose weights all sit outside Regular to Extra bold (a 900-only face)
 * still gets one option: the nearest picker weight, which the browser draws
 * with the real face rather than a faked one.
 */
export function headingWeightsFor(weights: readonly number[] | null): HeaderWeight[] {
  if (!weights || weights.length === 0) return [...ALL];
  const has = HEADING_WEIGHTS.filter((w) => weights.includes(w.css)).map((w) => w.value);
  if (has.length) return has;
  const nearest = weights.reduce((best, n) => (Math.abs(n - 600) < Math.abs(best - 600) ? n : best), weights[0]);
  return [nearestHeadingWeightTo(nearest, ALL)];
}

function nearestHeadingWeightTo(css: number, options: readonly HeaderWeight[]): HeaderWeight {
  let best = options[0];
  let gap = Infinity;
  for (const o of options) {
    const d = Math.abs(headingWeightCss(o) - css);
    // On a tie the heavier wins: a heading picked at Semibold between Regular
    // and Extra bold was meant to stand out.
    if (d < gap || (d === gap && headingWeightCss(o) > headingWeightCss(best))) {
      best = o;
      gap = d;
    }
  }
  return best;
}

/** The saved weight if the font has it, else the nearest one it has. */
export function nearestHeadingWeight(saved: HeaderWeight | string | null | undefined, options: readonly HeaderWeight[]): HeaderWeight {
  const want = (ALL as string[]).includes(String(saved)) ? (saved as HeaderWeight) : 'bold';
  if (options.length === 0 || options.includes(want)) return want;
  return nearestHeadingWeightTo(headingWeightCss(want), options);
}

type HeadingFontOpts = {
  fontStyle: FontStyle | string | null | undefined;
  headingFont?: string | null;
  uploadedHeading?: UploadedFontWeights | null;
};

/**
 * The weights the heading font really has, picked the way ThemedEventPage
 * picks the font: built-in styles have every one; "Your own" draws an
 * uploaded heading face when there is one, else the chosen font.
 */
export function realHeadingWeights(opts: HeadingFontOpts): HeaderWeight[] {
  if (opts.fontStyle !== 'custom') return [...ALL];
  return headingWeightsFor(fontWeightsOf(opts.headingFont || null, opts.uploadedHeading ?? null));
}

/**
 * The weights the pickers offer: all of them. One the font lacks is drawn
 * with an outline (headingWeightDrawing), so Bold on a font with only a
 * Regular still reads as bold, the same on every device.
 */
export function headingWeightOptions(opts: HeadingFontOpts): HeaderWeight[] {
  void opts;
  return [...ALL];
}

/** The real weight the page draws headings at: the saved one, snapped to what the heading font has. */
export function resolveHeadingWeight(opts: HeadingFontOpts & { saved: HeaderWeight | string | null | undefined }): HeaderWeight {
  return nearestHeadingWeight(opts.saved, realHeadingWeights(opts));
}

/** Outline width per 100 of weight the font is missing, in em. Close to the weight a browser's own fake bold adds, without the doubled letters. */
const STROKE_EM_PER_100 = 0.014;

/**
 * How the page draws headings: `css` is a weight the font has (never one the
 * browser would fake), and `strokeEm` the outline that makes up the rest when
 * the chosen weight is heavier than anything the font has. Zero when the font
 * has the weight, or something heavier.
 */
export function headingWeightDrawing(opts: HeadingFontOpts & { saved: HeaderWeight | string | null | undefined }): { css: number; strokeEm: number } {
  const want = headingWeightCss(nearestHeadingWeight(opts.saved, ALL));
  const css = headingWeightCss(resolveHeadingWeight(opts));
  const missing = Math.max(0, want - css);
  return { css, strokeEm: Math.round((missing / 100) * STROKE_EM_PER_100 * 1000) / 1000 };
}

/**
 * The Google Fonts stylesheet for a family at the weights wanted, asking only
 * for weights the family has (css2 answers 400 for the whole request when none
 * of them exist). Null for a font every device already has.
 *
 * An unknown family is asked for Regular and Bold: css2 serves whichever of a
 * list exists, so this loads at least one face for nearly every family.
 */
export function googleFontsCss2Url(family: string | null | undefined, wanted: readonly number[] = [400, 700]): string | null {
  const name = (family ?? '').trim().replace(/^["']|["']$/g, '');
  if (!name || isSystemFont(name)) return null;
  const has = GOOGLE_FONT_WEIGHTS[key(name)];
  let weights: number[];
  if (has) {
    weights = [...new Set(wanted)].filter((w) => has.includes(w));
    if (weights.length === 0) {
      // None of the wanted weights: the nearest one the family has, per wanted weight.
      weights = [...new Set(wanted.map((w) => has.reduce((b, n) => (Math.abs(n - w) < Math.abs(b - w) ? n : b), has[0])))];
    }
  } else {
    weights = [400, 700];
  }
  weights.sort((a, b) => a - b);
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}:wght@${weights.join(';')}&display=swap`;
}

/**
 * The font-weight range an uploaded face is declared with. Today's 100 500 for
 * a Regular (or unknown) master keeps iOS matching the face and lets the
 * browser embolden bold buttons; a heavier master is declared up to its own
 * weight so it is drawn as designed, never emboldened again on top; a variable
 * font is declared across its whole axis.
 */
export function uploadedFontWeightRange(weights: readonly number[] | null | undefined): string {
  const w = (Array.isArray(weights) ? weights : []).filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  const top = w.length ? Math.max(...w) : 0;
  return `100 ${Math.max(500, Math.min(1000, top))}`;
}
